import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface Honorario {
  id: string;
  procedimento: string;
  codigo: string;
  categoria: string;
  valor_particular: number;
  valor_convenio: number;
  custo_direto?: number;
  duracao_media: number;
  status: string;
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
}

export const useHonorarios = () => {
  const [honorarios, setHonorarios] = useState<Honorario[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const activeClinicaId = useActiveClinicaId();

  const fetchHonorarios = async () => {
    try {
      setLoading(true);
      const data = await apiList<Honorario>("honorarios");
      setHonorarios(data.sort((a, b) => a.procedimento.localeCompare(b.procedimento)));
    } catch (error: any) {
      toast({
        title: "Erro ao carregar honorários",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createHonorario = async (honorario: Omit<Honorario, "id" | "created_at" | "updated_at">) => {
    try {
      setLoading(true);
      await apiCreate<Honorario>("honorarios", honorario);

      toast({
        title: "Honorário cadastrado",
        description: "Procedimento cadastrado com sucesso!",
      });

      await fetchHonorarios();
    } catch (error: any) {
      toast({
        title: "Erro ao cadastrar honorário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateHonorario = async (id: string, honorario: Partial<Honorario>) => {
    try {
      setLoading(true);
      await apiUpdate<Honorario>("honorarios", id, honorario);

      toast({
        title: "Honorário atualizado",
        description: "Procedimento atualizado com sucesso!",
      });

      await fetchHonorarios();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar honorário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteHonorario = async (id: string) => {
    try {
      setLoading(true);
      await apiDelete("honorarios", id);

      toast({
        title: "Honorário excluído",
        description: "Procedimento excluído com sucesso!",
      });

      await fetchHonorarios();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir honorário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    honorarios,
    loading,
    fetchHonorarios,
    activeClinicaId,
    createHonorario,
    updateHonorario,
    deleteHonorario,
  };
};
